import { useEffect, useRef, useImperativeHandle } from 'react';
import { useGroupKey, useJoinKeyPathToString, useNoOutlineWhenFocused, useRegisterInlineFieldEditor, useWasUndoRedoEventWithData } from '../lib/InlineFieldEditingHooks';
import { setCaretPosition } from '../lib/domUtils/selectionDomUtils';
// @ts-expect-error not typed
import EditorConfigSingleton from 'ContentEditorUI/EditorConfigSingleton';
// Helper function to sanitize input to only allow numbers and decimal point
const sanitizeNumberInput = input => {
  // Remove any characters that aren't digits or decimal point
  let sanitized = input.replace(/[^\d.]/g, '');

  // Ensure only one decimal point exists
  const decimalPoints = sanitized.match(/\./g) || [];
  if (decimalPoints.length > 1) {
    const firstDecimalIndex = sanitized.indexOf('.');
    sanitized = sanitized.substring(0, firstDecimalIndex + 1) + sanitized.substring(firstDecimalIndex + 1).replace(/\./g, '');
  }
  return sanitized;
};
const NumberFieldEditor = ({
  name,
  node,
  onUpdate,
  currentValue,
  keyPath,
  label,
  helpText,
  isRequired = false,
  requiredForSaving = false,
  supportsInlinePlaceholdersAndLabels,
  moduleName,
  onFocusCallback,
  onBlurCallback,
  registerInlineFieldRef
}) => {
  const textRef = useRef(null);
  useNoOutlineWhenFocused(node);
  useRegisterInlineFieldEditor(moduleName);
  const fieldKeyString = useJoinKeyPathToString(keyPath);
  const groupKey = useGroupKey(moduleName, fieldKeyString, name);
  const wasUndoRedo = useWasUndoRedoEventWithData(currentValue);
  useImperativeHandle(textRef, () => {
    return {
      focus: () => {
        if (document.activeElement !== node) {
          node.focus();
          // We don't have click coordinates from the inline toolbar or sidebar
          // so default to manually clicking the top right of the field
          const {
            right,
            top
          } = node.getBoundingClientRect();
          setCaretPosition(right - 5, top + 5);
          onFocusCallback();
        }
      }
    };
  }, [node, onFocusCallback]);

  // @TODO: This may fall apart once we have modules with inline & non-inline fields
  // that experience a live update via Remy (because the ref depends on the module DOM node)
  useEffect(() => {
    registerInlineFieldRef === null || registerInlineFieldRef === void 0 || registerInlineFieldRef(name, 'number', textRef.current);
  }, [registerInlineFieldRef, name]);
  useEffect(() => {
    if (!supportsInlinePlaceholdersAndLabels) {
      return;
    }
    const inpageConfig = EditorConfigSingleton.get('inpage');
    if (inpageConfig && inpageConfig.shouldUseInlinePlaceholdersAndLabels) {
      // Setting data attributes so our css can render psuedo placeholders with the text on
      // the attributes when the field is empty
      if (helpText) {
        node.setAttribute('data-hs-inline-help-text', helpText);
      }
      if (label) {
        node.setAttribute('data-hs-inline-label', label);
        if (isRequired) {
          // Note: setting a value of '*' instead of `true` because the pseudo elements render the raw string
          // that lives on the data attribute
          node.setAttribute('data-hs-inline-label-required', '*');
        }
      }
    }
  }, [helpText, isRequired, label, node, supportsInlinePlaceholdersAndLabels]);

  // Set content editble on provided node + change listeners on them
  useEffect(() => {
    let textBeforeEditing = null;
    const updateListener = e => {
      const target = e.target;
      let updatedText = target.innerText;

      // Sanitize the input to only allow numbers and decimal point
      const sanitizedText = sanitizeNumberInput(updatedText);

      // Update the DOM node with the sanitized text if it's different
      if (sanitizedText !== updatedText) {
        target.innerText = sanitizedText;
        updatedText = sanitizedText;
      }
      if (requiredForSaving && updatedText === '') {
        return;
      }
      onUpdate(keyPath, updatedText, {
        groupKey
      });
    };
    const onClick = e => {
      if (document.activeElement !== node) {
        textBeforeEditing = node.innerText;
        // If the field is required for saving, check to see if the current value
        // is equal to the translated help text. If it is, assume that the value is
        // placeholder content and clear it from the DOM on node focus.
        if (requiredForSaving && helpText && textBeforeEditing === helpText) {
          node.innerText = '';
        }
        node.focus();
        setCaretPosition(e.clientX, e.clientY);
        onFocusCallback();
      }
    };
    const onBlur = () => {
      // If the field is required for saving and they're attempting to save an empty
      // string, replace the empty string with the content they had prior to making
      // any updates and save that old value again
      if (requiredForSaving && node.innerText === '' && textBeforeEditing) {
        node.innerText = textBeforeEditing;
        onUpdate(keyPath, textBeforeEditing, {
          groupKey
        });
      }
      textBeforeEditing = null;
      onBlurCallback();
    };
    const onPaste = e => {
      if (e.clipboardData) {
        e.preventDefault();

        // Get pasted content and sanitize it
        const paste = e.clipboardData.getData('text/plain');
        const sanitizedPaste = sanitizeNumberInput(paste);
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) {
          return;
        }
        selection.deleteFromDocument();
        selection.getRangeAt(0).insertNode(document.createTextNode(sanitizedPaste));
        selection.collapseToEnd();
        node.normalize();
        onUpdate(keyPath, node.innerText, {
          groupKey
        });
      }
    };
    const onKeyDown = e => {
      // Allow: special keys, navigation keys, backspace, delete, tab, escape
      const specialKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

      // Allow ctrl/cmd+a, ctrl/cmd+c, ctrl/cmd+v, ctrl/cmd+x
      const ctrlCombos = ['a', 'c', 'v', 'x'];
      if (specialKeys.includes(e.key) || e.ctrlKey && ctrlCombos.includes(e.key.toLowerCase()) || e.metaKey && ctrlCombos.includes(e.key.toLowerCase())) {
        // Let the event proceed
        return;
      }

      // Allow numeric keys and decimal point
      if (/^[0-9.]$/.test(e.key)) {
        // If it's a decimal point, check if one already exists
        if (e.key === '.' && node.innerText.includes('.')) {
          e.preventDefault();
        }
        // Else let the event proceed
        return;
      }

      // Prevent any other key
      e.preventDefault();
    };
    node.setAttribute('contenteditable', 'true');
    node.addEventListener('input', updateListener);
    node.addEventListener('click', onClick);
    node.addEventListener('paste', onPaste);
    node.addEventListener('blur', onBlur);
    node.addEventListener('keydown', onKeyDown);
    return () => {
      node.removeEventListener('input', updateListener);
      node.removeEventListener('click', onClick);
      node.removeEventListener('blur', onBlur);
      node.removeEventListener('keydown', onKeyDown);
      node.removeEventListener('paste', onPaste);
      node.removeAttribute('contenteditable');
    };
  }, [name, node, onUpdate, keyPath, onFocusCallback, onBlurCallback, groupKey, requiredForSaving, helpText]);

  // Make sure updates from the sidebar are also applied to the DOM
  useEffect(() => {
    if (currentValue !== undefined && currentValue !== null && node.innerText !== currentValue && (
    // Always apply the `currentValue` if the change came from an undo/redo action
    document.activeElement !== node || wasUndoRedo)) {
      // Sanitize the current value coming from store
      const sanitizedValue = sanitizeNumberInput(currentValue.toString());
      node.innerText = sanitizedValue;
    }
  }, [currentValue, node, wasUndoRedo]);
  return null;
};
export default NumberFieldEditor;